import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import NoItemsFound from '../../../components/NoItemsFound/NoItemsFound';
import TableHeader from '../../../components/Common/TableHeader';
import { convertTimestampToDateTime } from '../../../helper/helper';
import { Badge } from '../PhysicalBookOrder/components/FulfillmentStatusBadges';
import RefreshButton from '../../../components/Admin/RefreshButton';
import { Tone } from '../PhysicalBookOrder/helpers/orderDisplay';
import {
  cancelPrintBatch,
  dispatchPrintBatch,
  fetchPrintBatches,
  PrintBatch,
} from './services/PrintBatches.service';

const STATUS_BADGE: Record<PrintBatch['status'], { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'blue' },
  dispatched: { label: 'Dispatched', tone: 'amber' },
  acknowledged: { label: 'Acknowledged', tone: 'green' },
  partially_failed: { label: 'Partially failed', tone: 'red' },
  failed: { label: 'Failed', tone: 'red' },
  canceled: { label: 'Canceled', tone: 'gray' },
};

const PrintBatches: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [batches, setBatches] = useState<PrintBatch[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setBatches(await fetchPrintBatches());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDispatch = async (batch: PrintBatch) => {
    const verb = batch.status === 'draft' ? 'Dispatch' : 'Re-dispatch';
    if (!window.confirm(`${verb} print job #${batch.batchNumber} (${batch.orderCount} orders) to the print webhook?`)) return;
    await dispatchPrintBatch(batch.id, setBusy);
    await load();
  };

  const handleCancel = async (batch: PrintBatch) => {
    if (!window.confirm(`Cancel print job #${batch.batchNumber}? Its orders return to Ready to dispatch.`)) return;
    await cancelPrintBatch(batch.id, setBusy);
    await load();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-xs text-gray-500">
          Print jobs go to the print webhook (N8N) as one payload — it zips the PDFs, emails the printer, and acknowledges
          back per order. Create them from All Orders (select Ready-to-dispatch rows) or an order card — they dispatch
          immediately; use this screen to monitor, re-dispatch failures, or cancel.
        </p>
        <RefreshButton onClick={load} loading={loading || busy} className="shrink-0" />
      </div>
      {batches.length === 0 ? (
        <NoItemsFound message="No print jobs yet — create one from All Orders" />
      ) : (
        <div className="w-full overflow-auto">
          <table className="min-w-full border border-gray-300">
            {TableHeader(['Print job #', 'Status', 'Orders', 'Created', 'By', 'Dispatched', 'Last error', ' '])}
            <tbody>
              {batches.map((b, index) => (
                <React.Fragment key={b.id}>
                  <tr className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                    <td className="py-2 text-left px-4 border-b font-semibold">#{b.batchNumber}</td>
                    <td className="py-2 text-left px-4 border-b">
                      <Badge tone={STATUS_BADGE[b.status]?.tone || 'gray'}>{STATUS_BADGE[b.status]?.label || b.status}</Badge>
                    </td>
                    <td className="py-2 text-left px-4 border-b">{b.orderCount}</td>
                    <td className="py-2 text-left px-4 border-b whitespace-nowrap">{convertTimestampToDateTime(b.createdAt)}</td>
                    <td className="py-2 text-left px-4 border-b max-w-xs truncate" title={b.createdBy}>
                      {b.createdBy}
                    </td>
                    <td className="py-2 text-left px-4 border-b whitespace-nowrap">
                      {b.dispatchedAt ? convertTimestampToDateTime(b.dispatchedAt) : '—'}
                    </td>
                    <td className="py-2 text-left px-4 border-b max-w-xs truncate text-red-700" title={b.lastError}>
                      {b.lastError || '—'}
                    </td>
                    <td className="py-2 text-center px-4 border-b whitespace-nowrap">
                      <button
                        type="button"
                        className="text-storyblue underline text-xs mr-3"
                        onClick={() => setExpanded(expanded === b.id ? null : b.id)}
                      >
                        {expanded === b.id ? 'Hide' : 'Orders'}
                      </button>
                      {['draft', 'failed', 'partially_failed'].includes(b.status) && (
                        <button
                          type="button"
                          disabled={busy}
                          className="bg-storygreen text-white font-semibold rounded-md px-3 py-1 text-xs mr-2 disabled:opacity-50"
                          onClick={() => handleDispatch(b)}
                        >
                          {b.status === 'draft' ? 'Dispatch' : 'Re-dispatch'}
                        </button>
                      )}
                      {['draft', 'failed'].includes(b.status) && (
                        <button
                          type="button"
                          disabled={busy}
                          className="bg-red-600 text-white font-semibold rounded-md px-3 py-1 text-xs disabled:opacity-50"
                          onClick={() => handleCancel(b)}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === b.id && (
                    <tr>
                      <td colSpan={8} className="px-6 py-3 border-b bg-gray-50">
                        <table className="min-w-full text-sm">
                          <tbody>
                            {b.orders.map((o) => (
                              <tr key={o.orderNumber}>
                                <td className="py-1 pr-4 font-semibold">{o.orderNumber}</td>
                                <td className="py-1 pr-4 max-w-xs truncate" title={o.ownerEmail}>
                                  {o.ownerEmail}
                                </td>
                                <td className="py-1 pr-4 max-w-xs truncate" title={o.bookTitle || o.bookId || ''}>
                                  {o.bookTitle || o.bookId || '—'} {o.quantity ? `× ${o.quantity}` : ''}
                                </td>
                                <td className="py-1 pr-4">
                                  {o.pdfUrl ? (
                                    <a href={o.pdfUrl} target="_blank" rel="noreferrer" className="text-storygreen underline">
                                      PDF
                                    </a>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="py-1">
                                  {o.result === 'ok' && <Badge tone="green">printed</Badge>}
                                  {o.result === 'failed' && (
                                    <span title={o.resultError}>
                                      <Badge tone="red">failed</Badge>
                                    </span>
                                  )}
                                  {!o.result && <Badge tone="gray">pending</Badge>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PrintBatches;
